/**
 * Keycloak Integration for Admin Panel
 */

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL || 'https://auth.flowcore.io';
const KEYCLOAK_REALM = import.meta.env.VITE_KEYCLOAK_REALM || 'memory-mesh';
const KEYCLOAK_ISSUER = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}`;
const KEYCLOAK_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'stroyma-app';
const KEYCLOAK_CLIENT_SECRET = import.meta.env.VITE_KEYCLOAK_CLIENT_SECRET || '';
// Dynamic redirect URI based on current domain
const REDIRECT_URI = `${window.location.origin}/callback`;

/**
 * Decode JWT token
 */
function decodeJWT(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    return JSON.parse(decodeBase64UrlUtf8(parts[1]));
  } catch {
    return null;
  }
}

function decodeBase64UrlUtf8(value) {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));

  return new TextDecoder('utf-8').decode(bytes);
}

function normalizeText(value) {
  return typeof value === 'string' ? value.normalize('NFC') : value;
}

function clearStoredAuth() {
  localStorage.removeItem('filmssavn_token');
  localStorage.removeItem('filmssavn_refresh_token');
  localStorage.removeItem('filmssavn_user');
}

function isTokenForCurrentRealm(payload) {
  return payload?.iss === KEYCLOAK_ISSUER;
}

/**
 * Initialize Keycloak and check auth status
 */
export function initKeycloak() {
  const token = localStorage.getItem('filmssavn_token');
  const user = localStorage.getItem('filmssavn_user');

  if (token && user) {
    const tokenPayload = decodeJWT(token);
    if (!isTokenForCurrentRealm(tokenPayload)) {
      console.warn('[Admin] Stored token belongs to a different realm, clearing session');
      clearStoredAuth();
      return {
        isAuthenticated: false,
        token: null,
        user: null,
      };
    }

    return {
      isAuthenticated: true,
      token: token,
      user: JSON.parse(user),
    };
  }

  return {
    isAuthenticated: false,
    token: null,
    user: null,
  };
}

/**
 * Redirect to Keycloak login
 */
export function login() {
  const state = generateRandomString(32);
  const nonce = generateRandomString(32);

  localStorage.setItem('oauth_state', state);
  localStorage.setItem('oauth_nonce', nonce);

  console.log('🔐 Login Debug:', {
    keycloakUrl: KEYCLOAK_URL,
    realm: KEYCLOAK_REALM,
    clientId: KEYCLOAK_CLIENT_ID,
    redirectUri: REDIRECT_URI,
    windowOrigin: window.location.origin,
  });

  const authUrl = new URL(`${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/auth`);
  authUrl.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);
  authUrl.searchParams.set('redirect_uri', REDIRECT_URI);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', 'openid profile email');
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('nonce', nonce);

  console.log('🔐 Full Auth URL:', authUrl.toString());

  window.location.href = authUrl.toString();
}

/**
 * Handle OAuth callback
 */
export async function handleCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');

  const savedState = localStorage.getItem('oauth_state');

  if (!code || state !== savedState) {
    throw new Error('Invalid OAuth callback');
  }

  // Exchange code for token
  const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;

  const params = new URLSearchParams();
  params.set('grant_type', 'authorization_code');
  params.set('code', code);
  params.set('client_id', KEYCLOAK_CLIENT_ID);
  params.set('redirect_uri', REDIRECT_URI);
  if (KEYCLOAK_CLIENT_SECRET) {
    params.set('client_secret', KEYCLOAK_CLIENT_SECRET);
  }

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    throw new Error('Failed to exchange code for token');
  }

  const tokenData = await response.json();

  // Decode JWT token to validate structure. Authorization is controlled by
  // video-api application roles, not Keycloak role claims.
  const tokenPayload = decodeJWT(tokenData.access_token);
  if (!isTokenForCurrentRealm(tokenPayload)) {
    throw new Error('Received token from unexpected Keycloak realm');
  }

  // Get user info
  const userInfoResponse = await fetch(`${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/userinfo`, {
    headers: {
      Authorization: `Bearer ${tokenData.access_token}`,
    },
  });

  if (!userInfoResponse.ok) {
    throw new Error('Failed to get user info');
  }

  const userInfo = await userInfoResponse.json();

  const user = {
    id: userInfo.sub,
    email: normalizeText(userInfo.email || userInfo.preferred_username),
    firstName: normalizeText(userInfo.given_name),
    lastName: normalizeText(userInfo.family_name),
    role: 'user',
  };

  // Store token and user info
  localStorage.setItem('filmssavn_token', tokenData.access_token);
  localStorage.setItem('filmssavn_refresh_token', tokenData.refresh_token);
  localStorage.setItem('filmssavn_user', JSON.stringify(user));

  // Clean up OAuth state
  localStorage.removeItem('oauth_state');
  localStorage.removeItem('oauth_nonce');

  return { token: tokenData.access_token, user };
}

/**
 * Logout
 */
export function logout() {
  // Clear local storage
  clearStoredAuth();

  // Redirect to Keycloak logout
  // Note: id_token_hint is optional - Keycloak will still log out without it
  const logoutUrl = new URL(`${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/logout`);
  logoutUrl.searchParams.set('post_logout_redirect_uri', window.location.origin);
  logoutUrl.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);

  window.location.href = logoutUrl.toString();
}

/**
 * Get current user (from localStorage or API)
 */
export async function getCurrentUser() {
  // First check localStorage
  const cachedUser = localStorage.getItem('filmssavn_user');
  if (cachedUser) {
    const user = JSON.parse(cachedUser);

    // If we have a token, fetch fresh user data from API to get correct role from database
    const token = await getToken();
    if (token) {
      try {
        const response = await fetch('/api/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.user) {
            // Update localStorage with fresh user data (includes correct role from database)
            localStorage.setItem('filmssavn_user', JSON.stringify(data.user));
            return data.user;
          }
        }
      } catch (error) {
        console.warn('Failed to fetch user from API, using cached:', error);
      }
    }

    return user;
  }

  return null;
}

/**
 * Get auth token (with automatic refresh if expired)
 */
export async function getToken() {
  const token = localStorage.getItem('filmssavn_token');
  if (!token) return null;

  // Decode and check expiration
  try {
    const payload = decodeJWT(token);
    if (!isTokenForCurrentRealm(payload)) {
      clearStoredAuth();
      return null;
    }

    const now = Date.now() / 1000;
    const bufferTime = 120; // Refresh 2 minutes before expiration

    // If token is expired or about to expire, refresh it
    if (payload.exp && payload.exp < now + bufferTime) {
      console.log('[Admin] Token expiring, refreshing...');
      try {
        const newToken = await refreshToken();
        return newToken;
      } catch (error) {
        console.error('[Admin] Failed to refresh token:', error);
        // Token refresh failed, return null to force re-login
        return null;
      }
    }
  } catch (error) {
    console.error('[Admin] Error checking token expiration:', error);
  }

  return token;
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  const token = localStorage.getItem('filmssavn_token');
  const user = localStorage.getItem('filmssavn_user');

  if (!(token && user)) return false;

  // Check token expiration
  try {
    const payload = decodeJWT(token);
    if (!isTokenForCurrentRealm(payload)) {
      clearStoredAuth();
      return false;
    }

    const now = Date.now() / 1000;
    if (payload.exp && payload.exp < now) {
      // Token expired
      return false;
    }
  } catch (error) {
    console.error('[Admin] Error checking token:', error);
    return false;
  }

  return true;
}

/**
 * Refresh token
 */
export async function refreshToken() {
  const refreshToken = localStorage.getItem('filmssavn_refresh_token');

  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const tokenUrl = `${KEYCLOAK_URL}/realms/${KEYCLOAK_REALM}/protocol/openid-connect/token`;

  const params = new URLSearchParams();
  params.set('grant_type', 'refresh_token');
  params.set('refresh_token', refreshToken);
  params.set('client_id', KEYCLOAK_CLIENT_ID);
  if (KEYCLOAK_CLIENT_SECRET) {
    params.set('client_secret', KEYCLOAK_CLIENT_SECRET);
  }

  const response = await fetch(tokenUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  });

  if (!response.ok) {
    // Refresh failed, need to login again
    logout();
    throw new Error('Token refresh failed');
  }

  const tokenData = await response.json();
  const tokenPayload = decodeJWT(tokenData.access_token);
  if (!isTokenForCurrentRealm(tokenPayload)) {
    clearStoredAuth();
    throw new Error('Received token from unexpected Keycloak realm');
  }

  // Update stored tokens
  localStorage.setItem('filmssavn_token', tokenData.access_token);
  localStorage.setItem('filmssavn_refresh_token', tokenData.refresh_token);

  return tokenData.access_token;
}

/**
 * Helper to generate random string
 */
function generateRandomString(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
